/**
 * A tiny synthesizer.
 *
 * Wirespan's sounds are generated rather than loaded: the whole app is
 * precached for offline play, and a handful of audio files would outweigh
 * everything else in the bundle. Oscillators also suit the subject — the game
 * is about electricity, and these are literally tones and filtered noise.
 */

/** Browsers gate audio until the player has interacted with the page. */
type ContextFactory = () => AudioContext;

function defaultFactory(): AudioContext {
  const Ctor =
    globalThis.AudioContext ??
    (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) throw new Error('Web Audio is unavailable');
  return new Ctor();
}

export interface ToneOptions {
  /** Starting frequency in Hz. */
  freq: number;
  /** Glide to this frequency over the note, if given. */
  to?: number;
  type?: OscillatorType;
  /** Seconds. */
  duration?: number;
  /** Peak gain, 0..1, before the master fader. */
  gain?: number;
  /** Seconds to reach peak gain. */
  attack?: number;
  /** Seconds from the start of the note. */
  delay?: number;
  /** Cents. */
  detune?: number;
}

export interface NoiseOptions {
  duration?: number;
  gain?: number;
  /** Low-pass cutoff in Hz. */
  cutoff?: number;
  /** Sweep the cutoff to this frequency across the burst. */
  cutoffTo?: number;
  delay?: number;
  attack?: number;
}

/**
 * How long a 'running' context's clock may stand still, in ms, before we call
 * it wedged. Short enough that the first tap after returning to the page
 * recovers the sound, long enough that two cues inside one render quantum
 * never look like a stall.
 */
const STALL_MS = 250;

/**
 * Wraps an `AudioContext` and plays short one-shot voices. Every voice
 * disposes of itself when it finishes, so nothing accumulates.
 */
export class Synth {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;
  private failed = false;
  /** Peak-gain multiplier for the voice-cap duck; 1 outside `scaled()`. */
  private gainScale = 1;
  /** The resume() currently in flight, or `null` when none is. */
  private resuming: Promise<void> | null = null;
  /**
   * The context clock's last advance, read against the wall clock. A context
   * that reports 'running' while its own clock stands still is wedged, and
   * the pair is the only way to tell that apart from one that is merely idle.
   */
  private seenCT = 0;
  private seenAt = 0;

  constructor(
    private readonly volume = 0.32,
    private readonly factory: ContextFactory = defaultFactory,
  ) {}

  /** True once audio is running and a sound would actually be heard. */
  get ready(): boolean {
    return this.ctx !== null && this.ctx.state === 'running';
  }

  get unavailable(): boolean {
    return this.failed;
  }

  /**
   * Start or wake the audio context. Must be called from a user gesture the
   * first time, which is why nothing is created in the constructor.
   */
  resume(): void {
    if (this.failed) return;
    // After some interruptions WebKit hands back a context that reports
    // 'running' while its clock stands still — it looks healthy and outputs
    // silence for the life of the page. Closing it is the only cure, and the
    // rebuild happens below in this very call, so a cue asked for inside a
    // gesture gets its fresh context inside that gesture.
    if (this.ctx?.state === 'running' && this.wedged(this.ctx)) this.retire();
    if (this.ctx?.state === 'closed') this.discard();
    try {
      const ctx = (this.ctx ??= this.create());
      if (!this.master) {
        const gain = ctx.createGain();
        gain.gain.value = this.volume;
        gain.connect(ctx.destination);
        this.master = gain;
      }
      // iOS parks a backgrounded context in 'interrupted', not 'suspended',
      // so resume anything that isn't running. A post-interruption resume can
      // reject; that's transient, not "no Web Audio", so `failed` stays unset.
      if (ctx.state !== 'running' && ctx.state !== 'closed') {
        const attempt = ctx
          .resume()
          .then(() => {
            // A woken clock starts from wherever it froze; re-base the stall
            // watch so the next reading judges the context against now, not
            // against the moment it went under.
            if (this.ctx === ctx) this.rebase();
          })
          .catch(() => {});
        this.resuming = attempt;
        void attempt.finally(() => {
          // A newer resume may already be in flight; only clear our own.
          if (this.resuming === attempt) this.resuming = null;
        });
      }
    } catch {
      // No Web Audio here — the game is played silently.
      this.failed = true;
    }
  }

  /** Build a fresh context and arm its stall watch. */
  private create(): AudioContext {
    const ctx = this.factory();
    this.seenCT = ctx.currentTime;
    this.seenAt = performance.now();
    // WebKit fires statechange on an interruption and on the way back; either
    // way the stall watch must re-base, or the next reading judges a freshly
    // woken context against a clock from before it went under. Scoped to the
    // context it was bound to: a retired one still fires statechange as it
    // closes, and that is not news about its replacement.
    try {
      ctx.addEventListener('statechange', () => {
        if (this.ctx !== ctx) return;
        this.resuming = null;
        this.rebase();
        if (ctx.state === 'closed') this.discard();
      });
    } catch {
      // Older WebKit; the resume() above still re-bases on success.
    }
    return ctx;
  }

  /** Re-base the stall watch on the context's clock as of now. */
  private rebase(): void {
    if (!this.ctx) return;
    this.seenCT = this.ctx.currentTime;
    this.seenAt = performance.now();
  }

  /** True when a 'running' context's clock has stood still for STALL_MS. */
  private wedged(ctx: AudioContext): boolean {
    const t = ctx.currentTime;
    const now = performance.now();
    if (t > this.seenCT) {
      this.seenCT = t;
      this.seenAt = now;
      return false;
    }
    return now - this.seenAt >= STALL_MS;
  }

  /** Close a dead context; resume() rebuilds in the same pass. */
  private retire(): void {
    const ctx = this.ctx;
    this.discard();
    void ctx?.close().catch(() => {});
  }

  /** Forget the context and everything built against it. */
  private discard(): void {
    this.ctx = null;
    this.master = null;
    this.noiseBuffer = null;
    this.resuming = null;
  }

  /** Run `fn` now, or once the in-flight resume() settles — whichever is first. */
  afterResume(fn: () => void): void {
    if (this.resuming) void this.resuming.then(fn);
    else fn();
  }

  /** Nudge an existing context on returning to the foreground. Never creates
   *  one — that must wait for a user gesture. */
  wake(): void {
    if (this.ctx) this.resume();
  }

  /** Park the context while the page is hidden. Suspending ourselves keeps it
   *  in the standard 'suspended' state instead of iOS's flaky 'interrupted'. */
  suspend(): void {
    if (this.ctx?.state === 'running') this.ctx.suspend().catch(() => {});
  }

  /** Seconds until `delay` from now, on the audio clock. */
  private startAt(delay = 0): number {
    return (this.ctx?.currentTime ?? 0) + delay;
  }

  /** Every voice scheduled inside `fn` plays at `scale` of its written gain. */
  scaled(scale: number, fn: () => void): void {
    this.gainScale = scale;
    try {
      fn();
    } finally {
      this.gainScale = 1;
    }
  }

  tone(opts: ToneOptions): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master) return;

    const {
      freq,
      to,
      type = 'sine',
      duration = 0.18,
      gain = 0.5,
      attack = 0.005,
      delay = 0,
      detune = 0,
    } = opts;

    const t0 = this.startAt(delay);
    const osc = ctx.createOscillator();
    osc.type = type;
    osc.detune.value = detune;
    osc.frequency.setValueAtTime(freq, t0);
    if (to !== undefined)
      osc.frequency.exponentialRampToValueAtTime(Math.max(1, to), t0 + duration);

    const env = ctx.createGain();
    // Exponential decay to near-silence: ramping to 0 is undefined behaviour.
    env.gain.setValueAtTime(0.0001, t0);
    env.gain.exponentialRampToValueAtTime(Math.max(0.0001, gain * this.gainScale), t0 + attack);
    env.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);

    osc.connect(env).connect(master);
    osc.start(t0);
    osc.stop(t0 + duration + 0.02);
    osc.onended = () => {
      osc.disconnect();
      env.disconnect();
    };
  }

  /** Filtered white noise — the percussive half of the palette. */
  noise(opts: NoiseOptions = {}): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master) return;

    const {
      duration = 0.12,
      gain = 0.4,
      cutoff = 1800,
      cutoffTo,
      delay = 0,
      attack = 0.004,
    } = opts;

    const t0 = this.startAt(delay);
    const src = ctx.createBufferSource();
    src.buffer = this.buffer(ctx);

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(cutoff, t0);
    if (cutoffTo !== undefined) {
      filter.frequency.exponentialRampToValueAtTime(Math.max(1, cutoffTo), t0 + duration);
    }

    const env = ctx.createGain();
    env.gain.setValueAtTime(0.0001, t0);
    env.gain.exponentialRampToValueAtTime(Math.max(0.0001, gain * this.gainScale), t0 + attack);
    env.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);

    src.connect(filter).connect(env).connect(master);
    src.start(t0);
    src.stop(t0 + duration + 0.02);
    src.onended = () => {
      src.disconnect();
      filter.disconnect();
      env.disconnect();
    };
  }

  /** One second of white noise, generated once and replayed. */
  private buffer(ctx: AudioContext): AudioBuffer {
    if (this.noiseBuffer) return this.noiseBuffer;
    const frames = Math.floor(ctx.sampleRate);
    const buf = ctx.createBuffer(1, frames, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < frames; i++) data[i] = Math.random() * 2 - 1;
    this.noiseBuffer = buf;
    return buf;
  }
}
